import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/ui/Button'
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton'
import { toISODate } from '../../../lib/datetime'
import { rowActionLabel } from '../../../lib/deleteLabel'
import {
  DAY_KEYS,
  MINUTES_IN_DAY,
  MONTH_KEYS,
  layoutDay,
  weekdayIndex,
  type DayEvent,
} from '../week'
import EventChip from './EventChip'

interface Props {
  day: Date
  events: DayEvent[]
  isToday: boolean
  /** Geçmiş günde "+" çizilmez (geçmişe davet kaydedilemez). */
  canCreate: boolean
  loading: boolean
  /** Takvimin adresi (hafta dahil) — çip bağlantısı detaya taşır. */
  from: string
  onCreate: (isoDate: string) => void
  /** Saat ızgarası (masaüstü); değilse düz liste (dar ekran). */
  timeline?: boolean
  /** "Şimdi" çizgisinin dakikası; yalnız bugünün sütununa verilir. */
  nowMinute?: number
}

/** Dakika → gövde yüzdesi (gövde günün 24 saatinin tamamıdır). */
function pct(minutes: number): string {
  return `${(minutes / MINUTES_IN_DAY) * 100}%`
}

/**
 * Takvimin bir günü: başlık + "+" + toplantı çipleri. "+" düğmesinin adı günü taşır — yoksa ekran
 * okuyucu yedi aynı "Yeni davet" düğmesini ayırt edemez.
 */
export default function DayColumn({
  day,
  events,
  isToday,
  canCreate,
  loading,
  from,
  onCreate,
  timeline = false,
  nowMinute,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const iso = toISODate(day)
  const headingId = `agenda-day-${iso}`
  const weekday = t(`agenda.week.days.${DAY_KEYS[weekdayIndex(day)]}`)
  const dayLabel = t('agenda.week.dayLabel', {
    day: day.getDate(),
    month: t(`agenda.week.months.${MONTH_KEYS[day.getMonth()]}`),
    weekday,
  })
  const addLabel = rowActionLabel(t, 'agenda.week.newOn', dayLabel)

  const skeleton = loading && (
    <LoadingSkeleton lines={2} height="1.75rem" className="agenda-day-skeleton" />
  )
  const hasEvents = !loading && events.length > 0

  return (
    <section
      className={isToday ? 'agenda-day agenda-day--today' : 'agenda-day'}
      aria-labelledby={headingId}
      aria-current={isToday ? 'date' : undefined}
      data-date={iso}
    >
      <div className="agenda-day-head">
        <h3 id={headingId} className="agenda-day-title">
          <span className="agenda-day-name">{weekday}</span>{' '}
          <span className="agenda-day-number">{day.getDate()}</span>
        </h3>
        {canCreate && (
          <Button
            type="button"
            text
            rounded
            icon="pi pi-plus"
            className="agenda-day-add"
            aria-label={addLabel}
            title={addLabel}
            onClick={() => onCreate(iso)}
          />
        )}
      </div>
      {timeline ? (
        <div className="agenda-day-body">
          {skeleton}
          {hasEvents && (
            <ul className="agenda-day-events">
              {layoutDay(events, day).map((ev) => {
                const style: CSSProperties = {
                  top: pct(ev.top),
                  height: pct(ev.span),
                  left: `${(ev.column / ev.columns) * 100}%`,
                  width: `${100 / ev.columns}%`,
                }
                return (
                  <li key={ev.item.id} className="agenda-day-slot" style={style}>
                    <EventChip event={ev} from={from} />
                  </li>
                )
              })}
            </ul>
          )}
          {nowMinute !== undefined && (
            <div className="agenda-now" style={{ top: pct(nowMinute) }} aria-hidden="true" />
          )}
        </div>
      ) : (
        <>
          {skeleton}
          {hasEvents && (
            <ul className="agenda-day-events">
              {events.map((ev) => (
                <li key={ev.item.id}>
                  <EventChip event={ev} from={from} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}
